import { type MenuProductOption } from '@koala/sdk';
import {
  StyledModifierImage,
  StyledOptionContent,
  StyledOptionLabel,
  StyledOptionName,
  StyledOptionNamePriceRow,
  StyledOptionPrice,
  StyledOptionTextArea,
  StyledSelectionIndicator,
} from '../styles';
import { AllergensMenuCard } from '@/components/allergens';
import { ALLERGEN_CARD_MODE } from '@/constants/global';
import { useSelector } from '@/redux';
import { invertNumberIfOptionInverted, toDollars } from '@/utils/basket';
import { resizeImage } from '@/utils/imageHelper';
import { deriveCalorieDisplay } from '@/utils/menu';

interface Props {
  option: MenuProductOption;
  supportsQuantitySelection: boolean;
  isSingleSelect: boolean;
  isDisabled: boolean;
}

function SelectionIndicator({
  selected,
  isSingleSelect,
}: {
  selected: boolean;
  isSingleSelect: boolean;
}) {
  const size = 22;

  if (selected) {
    return (
      <StyledSelectionIndicator>
        <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
          {isSingleSelect ? (
            <circle cx="11" cy="11" r="11" fill="currentColor" />
          ) : (
            <rect width="22" height="22" rx="4" fill="currentColor" />
          )}
          <polyline
            points="7,11 10,14 15,8"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </StyledSelectionIndicator>
    );
  }

  return (
    <StyledSelectionIndicator>
      <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
        {isSingleSelect ? (
          <circle cx="11" cy="11" r="10" stroke="#CCCCCC" strokeWidth="1.5" fill="none" />
        ) : (
          <rect
            x="0.75"
            y="0.75"
            width="20.5"
            height="20.5"
            rx="3.5"
            stroke="#CCCCCC"
            strokeWidth="1.5"
            fill="none"
          />
        )}
      </svg>
    </StyledSelectionIndicator>
  );
}

export function Option({ option, supportsQuantitySelection, isSingleSelect, isDisabled }: Props) {
  const { webConfig } = useSelector(({ app }) => app.cmsConfig);
  const dietaryPreferencesEnabled = webConfig.menu.dietary_preferences_enabled;

  const validOptionCost = option.cost > 0 || option.cost < 0;
  const { cost, calories } = option;

  // Prep calorie parsing logic
  const { calories_separator, is_inverted, max_calories } = option;
  const calorieString: string = deriveCalorieDisplay(
    calories,
    max_calories,
    /** @TODO ensure that `calories_separator` is defined. */
    // @ts-expect-error
    calories_separator,
    is_inverted,
  );

  // Does this option have an image associated with it?
  const hasImage = Boolean(option?.images?.image_url_1_by_1);

  return (
    <>
      {hasImage && (
        <StyledModifierImage
          className="modifier-image"
          /** @TODO differentiate between `null`  and `undefined`. */
          // @ts-expect-error
          url={option?.images && resizeImage(option?.images?.image_url_1_by_1, { height: 200 })}
          supportsQuantitySelection={supportsQuantitySelection}
        />
      )}

      <StyledOptionContent
        data-css-override="StyledOptionContent"
        selected={!!option.selected}
        $is_inverted={!!option.is_inverted}
        $disabled={isDisabled}
      >
        <StyledOptionTextArea>
          <StyledOptionNamePriceRow>
            <StyledOptionName>
              <p style={{ fontWeight: option.selected ? 600 : 400 }}>
                {option.is_inverted ? option.name_inverted : option.name}
              </p>
              <StyledOptionLabel>
                {option.labels.map((label) => (
                  <img src={label.image_url} alt={label.image_description} key={label.name} />
                ))}
              </StyledOptionLabel>
            </StyledOptionName>

            {(calorieString || validOptionCost) && (
              <StyledOptionPrice>
                {calorieString}
                {calorieString && validOptionCost ? ' | ' : ''}
                {validOptionCost
                  ? `+$${toDollars(
                      invertNumberIfOptionInverted(cost, option.is_inverted ?? false),
                    )}`
                  : ''}
              </StyledOptionPrice>
            )}
          </StyledOptionNamePriceRow>

          {option.description && <p style={{ marginBottom: 10 }}>{option.description}</p>}
          {dietaryPreferencesEnabled && (
            <AllergensMenuCard productAllergens={option} mode={ALLERGEN_CARD_MODE.OPTIONS} />
          )}
        </StyledOptionTextArea>

        <SelectionIndicator selected={!!option.selected} isSingleSelect={isSingleSelect} />
      </StyledOptionContent>
    </>
  );
}
